import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { ENDPOINTS, VOLUME_LABELS } from '../../utils/constants';
import Alert from '../../components/common/Alert';
import Loading from '../../components/common/Loading';
import { formatDate } from '../../utils/formatters';
import './EmployeePage.css';
import './AccountApprovals.css';

const AccountApprovals = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processingId, setProcessingId] = useState(null);

  const fetchPending = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get(ENDPOINTS.AUTH.PENDING_ACCOUNTS);
      setAccounts(res.data);
    } catch {
      setError('Failed to load pending accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async (id, name) => {
    setProcessingId(id);
    try {
      await api.post(ENDPOINTS.AUTH.APPROVE_ACCOUNT(id));
      setAccounts(prev => prev.filter(a => a.id !== id));
      setAlert({ type: 'success', title: `${name}'s account has been approved.` });
    } catch {
      setAlert({ type: 'error', title: 'Failed to approve account.' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id, name) => {
    if (!rejectReason.trim()) return;
    setProcessingId(id);
    try {
      await api.post(ENDPOINTS.AUTH.REJECT_ACCOUNT(id), { reason: rejectReason.trim() });
      setAccounts(prev => prev.filter(a => a.id !== id));
      setRejectingId(null);
      setRejectReason('');
      setAlert({ type: 'success', title: `${name}'s account has been rejected.` });
    } catch {
      setAlert({ type: 'error', title: 'Failed to reject account.' });
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) return <div className="employee-page"><Loading /></div>;

  return (
    <div className="employee-page approvals-page">
      <div className="employee-page__header">
        <p className="employee-page__eyebrow">Account Management</p>
        <h1 className="employee-page__title">Account Approvals</h1>
      </div>

      {alert && (
        <div className="emp-list__alert">
          <Alert type={alert.type} title={alert.title} onDismiss={() => setAlert(null)} />
        </div>
      )}

      {error && (
        <div className="emp-list__alert">
          <Alert type="error" title={error} onDismiss={() => setError(null)} />
        </div>
      )}

      {accounts.length === 0 ? (
        <div className="employee-page__body">
          <div className="employee-page__placeholder-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </div>
          <p className="employee-page__placeholder">No pending applications</p>
        </div>
      ) : (
        <div className="approvals-page__list">
          {accounts.map(account => {
            const fullName = [account.first_name, account.last_name].filter(Boolean).join(' ') || 'Unnamed';
            const isRejecting = rejectingId === account.id;
            const isProcessing = processingId === account.id;

            return (
              <div key={account.id} className="approvals-page__card">
                <div className="approvals-page__card-header">
                  <div className="approvals-page__card-info">
                    <h3 className="approvals-page__card-name">{fullName}</h3>
                  </div>
                  <span className="approvals-page__card-date">{formatDate(account.created_at)}</span>
                </div>

                <div className="approvals-page__card-details">
                  {account.company_name && (
                    <div className="approvals-page__detail">
                      <span className="approvals-page__detail-label">Company</span>
                      <span className="approvals-page__detail-value">{account.company_name}</span>
                    </div>
                  )}
                  <div className="approvals-page__detail">
                    <span className="approvals-page__detail-label">Email</span>
                    <span className="approvals-page__detail-value">{account.email}</span>
                  </div>
                  {account.phone && (
                    <div className="approvals-page__detail">
                      <span className="approvals-page__detail-label">Phone</span>
                      <span className="approvals-page__detail-value">{account.phone}</span>
                    </div>
                  )}
                  {account.signup_source && (
                    <div className="approvals-page__detail">
                      <span className="approvals-page__detail-label">Source</span>
                      <span className={`approvals-page__source-badge approvals-page__source-badge--${account.signup_source}`}>
                        {account.signup_source === 'quickbooks_import' ? 'QuickBooks Import'
                          : account.signup_source === 'portal' ? 'New Application'
                          : account.signup_source}
                      </span>
                    </div>
                  )}
                </div>

                {/* Application qualification fields — only shown for portal signups */}
                {account.business_types?.length > 0 && (
                  <div className="approvals-page__qualification">
                    <p className="approvals-page__qualification-heading">Application Details</p>
                    <div className="approvals-page__qualification-grid">
                      <div className="approvals-page__detail">
                        <span className="approvals-page__detail-label">Business Type</span>
                        <span className="approvals-page__detail-value">
                          {account.business_types.map(t =>
                            t === 'Other' && account.business_type_other
                              ? `Other (${account.business_type_other})`
                              : t
                          ).join(', ')}
                        </span>
                      </div>
                      {account.estimated_monthly_volume && (
                        <div className="approvals-page__detail">
                          <span className="approvals-page__detail-label">Est. Volume</span>
                          <span className="approvals-page__detail-value">
                            {VOLUME_LABELS[account.estimated_monthly_volume] || account.estimated_monthly_volume}
                          </span>
                        </div>
                      )}
                      {account.has_loading_dock !== null && account.has_loading_dock !== undefined && (
                        <div className="approvals-page__detail">
                          <span className="approvals-page__detail-label">Loading Dock</span>
                          <span className="approvals-page__detail-value">{account.has_loading_dock ? 'Yes' : 'No'}</span>
                        </div>
                      )}
                      {account.current_supplier && (
                        <div className="approvals-page__detail">
                          <span className="approvals-page__detail-label">Current Supplier</span>
                          <span className="approvals-page__detail-value">{account.current_supplier}</span>
                        </div>
                      )}
                      {account.application_flour_types?.length > 0 && (
                        <div className="approvals-page__detail approvals-page__detail--full">
                          <span className="approvals-page__detail-label">Flour Types</span>
                          <span className="approvals-page__detail-value">{account.application_flour_types.join(', ')}</span>
                        </div>
                      )}
                      {account.how_did_you_hear && (
                        <div className="approvals-page__detail approvals-page__detail--full">
                          <span className="approvals-page__detail-label">How They Heard</span>
                          <span className="approvals-page__detail-value">{account.how_did_you_hear}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Sample requests */}
                {account.sample_requests?.length > 0 && (
                  <div className="approvals-page__qualification">
                    <p className="approvals-page__qualification-heading">Sample Requests</p>
                    <div className="approvals-page__sample-list">
                      {account.sample_requests.map((item, idx) => (
                        <div key={item.product_variant_id ?? idx} className="approvals-page__sample-item">
                          <span className="approvals-page__sample-name">
                            {item.product_name} — {item.variant_name}
                          </span>
                          {item.quantity > 1 && (
                            <span className="approvals-page__sample-qty">×{item.quantity}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isRejecting ? (
                  <div className="approvals-page__reject-form">
                    <label className="approvals-page__reject-label">Rejection Reason</label>
                    <textarea
                      className="approvals-page__reject-textarea"
                      value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      placeholder="Provide a reason for rejection…"
                      rows={3}
                      maxLength={1000}
                    />
                    <div className="approvals-page__reject-actions">
                      <button
                        className="approvals-page__btn approvals-page__btn--cancel"
                        onClick={() => { setRejectingId(null); setRejectReason(''); }}
                        disabled={isProcessing}
                      >
                        Cancel
                      </button>
                      <button
                        className="approvals-page__btn approvals-page__btn--reject-confirm"
                        onClick={() => handleReject(account.id, fullName)}
                        disabled={isProcessing || !rejectReason.trim()}
                      >
                        {isProcessing ? 'Rejecting…' : 'Confirm Rejection'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="approvals-page__actions">
                    <button
                      className="approvals-page__btn approvals-page__btn--approve"
                      onClick={() => handleApprove(account.id, fullName)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? 'Approving…' : 'Approve'}
                    </button>
                    <button
                      className="approvals-page__btn approvals-page__btn--reject"
                      onClick={() => { setRejectingId(account.id); setRejectReason(''); }}
                      disabled={isProcessing}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccountApprovals;
